import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/Skeleton';
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/SegmentedControl';
import { api, type DigestMode } from '@/lib/api';

import { FlatSection } from './FlatSection';
import { MemberCategoriesModal } from './team/MemberCategoriesModal';
import { useCategoryNames } from './team/useCategoryNames';

const MODES: DigestMode[] = ['instant', 'daily', 'off'];

/**
 * How this user personally receives alerts. Applies to the signed-in user only
 * — the tenant-wide channel per alert type is a separate, admin-level setting.
 * Saves on change, like the appearance controls above it.
 */
export function NotificationsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const preference = useQuery({
    queryKey: ['notification-preference'],
    queryFn: () => api.notifications.get(),
  });

  const save = useMutation({
    mutationFn: (mode: DigestMode) => api.notifications.update(mode),
    onSuccess: (updated) => {
      queryClient.setQueryData(['notification-preference'], updated);
    },
  });

  const mode = preference.data?.digest_mode ?? 'daily';

  // His own product groups. Here and not only on the team page, because a
  // member cannot open the team page -- and he is the one who knows them.
  const me = useQuery({ queryKey: ['auth-me'], queryFn: () => api.auth.me(), staleTime: 60_000 });
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const categoryNames = useCategoryNames(me.data?.category_ids);

  return (
    <FlatSection
      id="notifications"
      title={t('settings.notifications.heading')}
      note={t('settings.notifications.note')}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxWidth: 520 }}>
        <label className="text-text-secondary" style={{ fontSize: 13 }}>
          {t('settings.notifications.label')}
        </label>

        {preference.isPending ? (
          <Skeleton className="h-8 w-72 rounded-sm" />
        ) : (
          <SegmentedControl
            value={mode}
            onValueChange={(v) => v && save.mutate(v as DigestMode)}
          >
            {MODES.map((m) => (
              <SegmentedControlItem key={m} value={m} size="sm">
                {t(`settings.notifications.mode.${m}`)}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        )}

        <p className="text-text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
          {t(`settings.notifications.help.${mode}`)}
        </p>

        {save.isError && (
          <p className="text-danger" style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
            {t('settings.notifications.saveFailed')}
          </p>
        )}

        {me.data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 14 }}>
            <span className="text-text-secondary" style={{ fontSize: 13 }}>
              {t('settings.notifications.categories.label')}
            </span>
            <div className="flex items-center" style={{ gap: 12, fontSize: 13 }}>
              <span className="text-text-primary">
                {categoryNames.length > 0 ? categoryNames.join(' · ') : t('settings.notifications.categories.all')}
              </span>
              <button
                type="button"
                onClick={() => setCategoriesOpen(true)}
                className="text-text-secondary hover:text-accent"
                style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', fontSize: 12.5 }}
              >
                {t('settings.notifications.categories.change')}
              </button>
            </div>
            <p className="text-text-muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
              {t('settings.notifications.categories.help')}
            </p>
            <MemberCategoriesModal
              member={{
                id: me.data.user_id,
                email: me.data.email,
                name: me.data.name ?? me.data.email,
                role: me.data.role,
                is_active: true,
                is_pending: false,
                group_id: null,
                category_ids: me.data.category_ids ?? [],
              }}
              open={categoriesOpen}
              onOpenChange={setCategoriesOpen}
            />
          </div>
        )}
      </div>
    </FlatSection>
  );
}
